import { nflTeams, collegeTeams } from '../data/teams';
import { getFootballSeasonYear } from './season';

const PROVIDER = 'american_football';
const CACHE_TTL_MS = 30 * 60 * 1000;
const API_SPORTS_LEAGUES = {
  nfl: { id: 1, label: 'NFL' },
  college: { id: 2, label: 'College Football' },
};

const teamIdCache = new Map();
const scheduleCache = new Map();

const normalizeName = (value) => String(value || '')
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]/g, '');

const namesMatch = (candidate, requested) => {
  const a = normalizeName(candidate);
  const b = normalizeName(requested);
  if (!a || !b) return false;
  if (a === b) return true;
  // API-Sports often uses a shorter school name ("Oklahoma State") while
  // 40 Bags stores the full nickname ("Oklahoma State Cowboys").
  return Math.min(a.length, b.length) >= 5 && (a.includes(b) || b.includes(a));
};

const getFootballType = (teamName) => {
  if (nflTeams.some(team => team.name === teamName)) return 'nfl';
  if (collegeTeams.some(team => team.name === teamName)) return 'college';
  return null;
};

const getCached = (cache, key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.savedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const putCached = (cache, key, value) => {
  cache.set(key, { value, savedAt: Date.now() });
};

const apiErrors = (data) => {
  const errors = data?.errors;
  if (!errors) return [];
  if (Array.isArray(errors)) return errors.filter(Boolean).map(String);
  if (typeof errors === 'object') {
    return Object.entries(errors)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}: ${String(value)}`);
  }
  return [String(errors)];
};

const fetchAPI = async (endpoint, params = {}) => {
  const search = new URLSearchParams({ provider: PROVIDER, endpoint });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });

  const response = await fetch(`/api/api-sports-proxy?${search.toString()}`);
  let data = {};
  try {
    data = await response.json();
  } catch {
    // Keep the HTTP error below as the useful signal.
  }

  if (!response.ok) {
    // The proxy keeps upstream API-Sports error details nested under `details`
    // for non-2xx responses. Pull the useful, non-secret message back out so a
    // failed preview tells us whether this is auth, quota, or a bad parameter.
    const nestedErrors = apiErrors(data?.details || {});
    const message = nestedErrors.join('; ')
      || data?.details?.message
      || data?.message
      || data?.error
      || `HTTP ${response.status}`;
    throw new Error(`API-Sports ${endpoint} failed (${response.status}): ${message}`);
  }

  const errors = apiErrors(data);
  if (errors.length > 0) {
    throw new Error(`API-Sports ${endpoint} error: ${errors.join('; ')}`);
  }

  return data;
};

const getTeamObject = (entry) => entry?.team || entry || {};

const getSearchTerms = (teamName) => {
  const words = String(teamName || '').trim().split(/\s+/).filter(Boolean);
  const withoutNickname = words.length > 2 ? words.slice(0, -1).join(' ') : words.join(' ');
  return Array.from(new Set([withoutNickname, words.join(' ')].filter(term => term.length >= 3)));
};

const findTeamId = (entries, teamName) => {
  const teams = Array.isArray(entries) ? entries : [];
  const exact = teams.find(entry => {
    const team = getTeamObject(entry);
    return normalizeName(team.name) === normalizeName(teamName);
  });
  const loose = exact || teams.find(entry => namesMatch(getTeamObject(entry).name, teamName));
  return getTeamObject(loose).id || null;
};

const resolveTeamId = async (teamName, leagueId, season) => {
  const cacheKey = `${leagueId}:${season}:${normalizeName(teamName)}`;
  const cached = getCached(teamIdCache, cacheKey);
  if (cached) return cached;

  // Do a targeted name search instead of downloading every NCAA team. Besides
  // being much smaller, this avoids relying on a potentially paged team list.
  for (const searchTerm of getSearchTerms(teamName)) {
    const data = await fetchAPI('teams', {
      league: leagueId,
      season,
      search: searchTerm,
    });
    const teamId = findTeamId(data?.response, teamName);
    if (teamId) {
      putCached(teamIdCache, cacheKey, teamId);
      return teamId;
    }
  }

  throw new Error(`API-Sports team not found in league ${leagueId} season ${season}: ${teamName}`);
};

const parseWeek = (value) => {
  if (typeof value === 'number') return value;
  const match = String(value || '').match(/(\d+)(?!.*\d)/);
  return match ? Number(match[1]) : 0;
};

const formatKickoff = (time) => {
  const match = String(time || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return 'TBD';

  const hour24 = Number(match[1]);
  if (!Number.isFinite(hour24)) return 'TBD';
  const minute = match[2];
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minute} ${suffix} ET`;
};

const normalizeVenue = (venue) => {
  const name = venue?.name || '';
  const city = venue?.city || '';
  if (name && city && !name.toLowerCase().includes(city.toLowerCase())) {
    return `${name}, ${city}`;
  }
  return name || city || 'TBD';
};

const normalizeGame = (entry, selectedTeamId, selectedTeamName) => {
  const game = entry?.game || {};
  const home = entry?.teams?.home || {};
  const away = entry?.teams?.away || {};
  const selectedIsHome = String(home.id) === String(selectedTeamId) || namesMatch(home.name, selectedTeamName);
  const opponent = selectedIsHome ? away : home;
  const statusShort = game?.status?.short || '';

  return {
    date: game?.date?.date || '',
    opponent: opponent?.name || 'TBD',
    location: selectedIsHome ? 'home' : 'away',
    isHomeGame: selectedIsHome,
    venue: normalizeVenue(game?.venue),
    week: parseWeek(game?.week),
    kickoff: formatKickoff(game?.date?.time),
    status: game?.status?.long || statusShort || 'Scheduled',
    homeScore: entry?.scores?.home?.total ?? null,
    awayScore: entry?.scores?.away?.total ?? null,
    broadcast: null,
    isCompleted: ['FT', 'AOT'].includes(statusShort),
    gameResult: null,
    rivalry: false,
    conference: false,
    specialGame: null,
    dataSource: 'API-Sports',
  };
};

const todayInEastern = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

class APISportsScheduleAPI {
  static supportsTeam(teamName) {
    return Boolean(getFootballType(teamName));
  }

  static async getTeamSchedule(teamName) {
    const footballType = getFootballType(teamName);
    if (!footballType) return null;

    const league = API_SPORTS_LEAGUES[footballType];
    const season = getFootballSeasonYear();
    const cacheKey = `${footballType}:${season}:${normalizeName(teamName)}`;
    const cached = getCached(scheduleCache, cacheKey);
    if (cached) return cached;

    const teamId = await resolveTeamId(teamName, league.id, season);
    const data = await fetchAPI('games', {
      league: league.id,
      season,
      team: teamId,
      timezone: 'America/New_York',
    });

    const games = Array.isArray(data?.response) ? data.response : [];
    if (games.length === 0) {
      throw new Error(`API-Sports returned no ${season} games for ${teamName} (team ${teamId}, league ${league.id})`);
    }

    const normalized = games
      .map(game => normalizeGame(game, teamId, teamName))
      .filter(game => game.date)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (normalized.length === 0) {
      throw new Error(`API-Sports returned no usable ${season} games for ${teamName}`);
    }

    const result = {
      schedule: normalized,
      sport: league.label,
      provider: 'API-Sports',
    };
    putCached(scheduleCache, cacheKey, result);
    return result;
  }

  static async getUpcomingGames(teamName, limit = 6) {
    const teamSchedule = await this.getTeamSchedule(teamName);
    if (!teamSchedule) return null;

    const today = todayInEastern();
    const upcoming = teamSchedule.schedule.filter(game => game.date >= today);

    return {
      ...teamSchedule,
      schedule: upcoming.slice(0, limit),
      fullSchedule: teamSchedule.schedule,
    };
  }

  static clearCache() {
    teamIdCache.clear();
    scheduleCache.clear();
  }

  static getCacheSize() {
    return teamIdCache.size + scheduleCache.size;
  }
}

export default APISportsScheduleAPI;
